import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Star, Loader2, Check, RefreshCw, Folder, FolderOpen, ChevronRight, ChevronDown, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useOnshapeContext } from '@/hooks/useOnshapeContext';
import { listHardwareLibrary, browseHardwareFolders, setHardwareFavorite, insertLibraryHardware } from '@/lib/onshape-api';

const COLLAPSED_KEY = 'otto_hardware_collapsed';

const readCollapsed = () => {
  try {
    return JSON.parse(localStorage.getItem(COLLAPSED_KEY)) || {};
  } catch {
    return {};
  }
};

const matches = (doc, terms) => {
  const haystack = `${doc.name} ${doc.description} ${doc.folderPath.join(' ')}`.toLowerCase();
  return terms.every((t) => haystack.includes(t));
};

/**
 * Walk the user's Onshape drive and pick the library folder. Onshape's folder API
 * has no parent links, so the breadcrumb is the path clicked down from My Onshape.
 */
function FolderBrowser({ currentFolderId, onChoose, onCancel, busy }) {
  const [trail, setTrail] = useState([]); // [{id, name}] below My Onshape
  const [level, setLevel] = useState(null); // {folders, documentCount}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [link, setLink] = useState('');
  const here = trail[trail.length - 1];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    browseHardwareFolders(here?.id)
      .then((res) => !cancelled && setLevel(res))
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [here?.id]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Choose the Onshape folder that holds your hardware documents (one document per screw, nut, washer…).
        Subfolders are included.
      </p>

      <div className="rounded-md border">
        <nav className="flex flex-wrap items-center gap-0.5 border-b px-2 py-1.5 text-xs">
          {[{ id: null, name: 'My Onshape' }, ...trail].map((crumb, i, all) => (
            <span key={crumb.id || 'root'} className="flex items-center gap-0.5">
              {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
              {i === all.length - 1 ? (
                <span className="font-medium">{crumb.name}</span>
              ) : (
                <button type="button" className="text-muted-foreground hover:text-foreground hover:underline"
                  onClick={() => setTrail(trail.slice(0, i))}>
                  {crumb.name}
                </button>
              )}
            </span>
          ))}
        </nav>

        <div className="max-h-64 divide-y divide-border/70 overflow-y-auto">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {!loading && error && <p className="px-3 py-2 text-sm text-destructive">{error}</p>}
          {!loading && level?.folders.map((f) => (
            <button key={f.id} type="button" onClick={() => setTrail([...trail, f])}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/60">
              <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{f.name}</span>
              {f.id === currentFolderId && <span className="text-[11px] text-muted-foreground">current</span>}
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          ))}
          {!loading && !error && level && level.folders.length === 0 && (
            <p className="px-3 py-2 text-xs text-muted-foreground">No subfolders here.</p>
          )}
        </div>
      </div>

      <div className="flex gap-2">
        {onCancel && <Button variant="ghost" onClick={onCancel}>Cancel</Button>}
        <Button className="flex-1" disabled={!here || busy || loading} onClick={() => onChoose(here.id)}>
          {busy ? <Loader2 className="animate-spin" /> : <FolderOpen />}
          {here
            ? `Use ${here.name}${level ? ` (${level.documentCount} doc${level.documentCount === 1 ? '' : 's'} here)` : ''}`
            : 'Open a folder to use it'}
        </Button>
      </div>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (link.trim()) onChoose(link.trim());
        }}
      >
        <Input value={link} placeholder="Shared folder? Paste its link" onChange={(e) => setLink(e.target.value)} />
        <Button type="submit" variant="outline" disabled={busy || !link.trim()}>Use</Button>
      </form>
    </div>
  );
}

function HardwareRow({ doc, favorite, onToggleFavorite, onInsert, state }) {
  return (
    <div className="group hover:bg-muted/60">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onInsert(doc)}
          disabled={state?.busy}
          title="Insert into this assembly"
          className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-left"
        >
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm">{doc.name}</p>
            {(doc.description || doc.folderPath.length > 0) && (
              <p className="truncate text-[11px] text-muted-foreground">
                {doc.description || doc.folderPath.join(' / ')}
              </p>
            )}
          </div>
          {state?.busy && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />}
          {state?.done && <Check className="h-4 w-4 shrink-0 text-green-600" />}
        </button>
        <button
          type="button"
          onClick={() => onToggleFavorite(doc.documentId)}
          aria-label={favorite ? 'Remove from favorites' : 'Add to favorites'}
          className={`shrink-0 p-1.5 ${favorite ? 'text-amber-500' : 'text-muted-foreground opacity-0 group-hover:opacity-100 focus:opacity-100'}`}
        >
          <Star className="h-4 w-4" fill={favorite ? 'currentColor' : 'none'} />
        </button>
      </div>
      {state?.prompt}
      {state?.message && <p className="px-2 pb-1.5 text-[11px] text-muted-foreground">{state.message}</p>}
      {state?.error && <p className="px-2 pb-1.5 text-[11px] text-destructive">{state.error}</p>}
    </div>
  );
}

function Section({ title, children, collapsed, onToggle }) {
  const heading = 'text-[11px] font-semibold uppercase tracking-wide text-muted-foreground';
  return (
    <div className="space-y-1">
      {onToggle ? (
        <button type="button" onClick={onToggle} aria-expanded={!collapsed}
          className={`flex items-center gap-1 px-2 hover:text-foreground ${heading}`}>
          {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          {title}
        </button>
      ) : (
        <h2 className={`px-2 ${heading}`}>{title}</h2>
      )}
      {!collapsed && <div className="divide-y divide-border/70 border-y border-border/70">{children}</div>}
    </div>
  );
}

export function HardwareLibrary() {
  const { documentId, workspaceId, elementId, access, isValid } = useOnshapeContext();

  const [library, setLibrary] = useState(null); // response from listHardwareLibrary
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  // `${section}:${documentId}` → {busy, done, message, error, prompt}; keyed by section because a
  // favorite also shows under All, and the prompt should only open where it was clicked.
  const [rowState, setRowState] = useState({});
  const [changingFolder, setChangingFolder] = useState(false);
  const searchRef = useRef(null);

  const load = async (folderId, options) => {
    setLoading(true);
    setError('');
    try {
      const res = await listHardwareLibrary(folderId, options);
      setLibrary(res);
      setChangingFolder(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (library?.documents) searchRef.current?.focus(); }, [library?.documents]);

  const favorites = useMemo(() => new Set(library?.favorites || []), [library?.favorites]);
  const usage = library?.usage || {};
  const useCount = (id) => usage[id]?.count || 0;

  // Favorites first, then the ones you insert most, then alphabetical.
  const rank = (a, b) =>
    (favorites.has(b.documentId) - favorites.has(a.documentId)) ||
    (useCount(b.documentId) - useCount(a.documentId)) ||
    a.name.localeCompare(b.name, undefined, { numeric: true });

  const documents = library?.documents || [];
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  const searchResults = terms.length ? documents.filter((d) => matches(d, terms)).sort(rank) : null;
  const favoriteDocs = documents.filter((d) => favorites.has(d.documentId));
  // Everything that isn't a favorite: most recently inserted first, never-used ones alphabetically after.
  const recentDocs = documents
    .filter((d) => !favorites.has(d.documentId))
    .sort((a, b) => (usage[b.documentId]?.lastUsed || 0) - (usage[a.documentId]?.lastUsed || 0) ||
      a.name.localeCompare(b.name, undefined, { numeric: true }));

  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggleCollapsed = (section) => setCollapsed((c) => {
    const next = { ...c, [section]: !c[section] };
    try {
      localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next));
    } catch {
      // Collapse state is a convenience; ignore storage failures.
    }
    return next;
  });

  const setRow = (id, state) => setRowState((s) => ({ ...s, [id]: state }));

  const toggleFavorite = async (id) => {
    const on = !favorites.has(id);
    setLibrary((l) => ({ ...l, favorites: on ? [...l.favorites, id] : l.favorites.filter((f) => f !== id) }));
    try {
      await setHardwareFavorite(id, on);
    } catch (err) {
      setLibrary((l) => ({ ...l, favorites: on ? l.favorites.filter((f) => f !== id) : [...l.favorites, id] }));
      setError(err.message);
    }
  };

  const insert = async (doc, key, extra = {}) => {
    const id = doc.documentId;
    setRow(key, { busy: true });
    try {
      const res = await insertLibraryHardware({
        documentId, workspaceId, elementId, sourceDocumentId: id,
        sourceWorkspaceId: doc.workspaceId, sourceModifiedAt: doc.modifiedAt, ...extra,
      });

      if (res.needsVersion) {
        setRow(key, {
          prompt: (
            <div className="space-y-2 px-2 pb-2">
              <p className="text-[11px] text-muted-foreground">
                Assemblies can only use saved versions, and this document doesn't have one with a part in it yet.
              </p>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => insert(doc, key, { ...extra, createVersion: true })}>
                  Save {res.versionName} and insert
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setRow(key, null)}>Cancel</Button>
              </div>
            </div>
          ),
        });
        return;
      }

      if (res.needsPartChoice) {
        setRow(key, {
          prompt: (
            <div className="space-y-1 px-2 pb-2">
              <p className="text-[11px] text-muted-foreground">This document has {res.parts.length} parts. Insert which?</p>
              {res.parts.map((p) => (
                <Button key={p.partId} size="sm" variant="outline" className="w-full justify-start"
                  onClick={() => insert(doc, key, { ...extra, partIds: [p.partId] })}>
                  {p.name}
                </Button>
              ))}
              <Button size="sm" variant="outline" className="w-full justify-start"
                onClick={() => insert(doc, key, { ...extra, partIds: res.parts.map((p) => p.partId) })}>
                All {res.parts.length}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setRow(key, null)}>Cancel</Button>
            </div>
          ),
        });
        return;
      }

      const notes = [
        `Added ${res.inserted}${res.inserted !== res.requested ? ` of ${res.requested}` : ''} from ${res.version.name}` +
          (res.createdVersion ? ' (new version)' : '') + '. Placed at the origin.',
        res.hasUnversionedChanges && `${doc.name} has changes newer than ${res.version.name}; save a new version there to use them.`,
      ].filter(Boolean);
      setRow(key, { done: true, message: notes.join(' '), error: res.error });
      setLibrary((l) => ({
        ...l,
        usage: { ...l.usage, [id]: { count: (l.usage[id]?.count || 0) + 1, lastUsed: Date.now() } },
      }));
      setTimeout(() => setRowState((s) => (s[key]?.done ? { ...s, [key]: null } : s)), 6000);
    } catch (err) {
      setRow(key, { error: err.message });
    }
  };

  const row = (section) => (doc) => (
    <HardwareRow
      key={doc.documentId}
      doc={doc}
      favorite={favorites.has(doc.documentId)}
      onToggleFavorite={toggleFavorite}
      onInsert={(d) => insert(d, `${section}:${d.documentId}`)}
      state={rowState[`${section}:${doc.documentId}`]}
    />
  );

  if (!isValid) {
    return <p className="m-4 rounded-md bg-muted p-3 text-sm">Open Otto from an Onshape assembly to insert hardware.</p>;
  }

  const showSetup = library?.needsFolder || changingFolder;

  return (
    <div className="mx-auto max-w-xl space-y-4 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold">Hardware</h1>
          {library?.folder && !showSetup && (
            <p className="truncate text-xs text-muted-foreground">
              From <span className="font-medium">{library.folder.name}</span> ·{' '}
              <button type="button" className="underline" onClick={() => setChangingFolder(true)}>change</button>
            </p>
          )}
        </div>
        {library?.documents && !showSetup && (
          <Button variant="ghost" size="icon" aria-label="Refresh library" disabled={loading} onClick={() => load(null, { refresh: true })}>
            <RefreshCw className={loading ? 'animate-spin' : ''} />
          </Button>
        )}
      </div>

      {access === 'read' && (
        <p className="rounded-md bg-muted p-3 text-sm">This assembly is read-only. Switch to a workspace you can edit.</p>
      )}
      {error && <p className="rounded-md border border-destructive/50 p-3 text-sm text-destructive">{error}</p>}

      {loading && !library && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your library…
        </div>
      )}

      {showSetup && (
        <FolderBrowser
          currentFolderId={library?.folder?.id}
          onChoose={(f) => load(f)}
          onCancel={library?.folder ? () => setChangingFolder(false) : null}
          busy={loading}
        />
      )}

      {library?.documents && !showSetup && (
        <>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchRef}
                value={query}
                placeholder="Search hardware"
                className="pl-8"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchResults?.length) insert(searchResults[0], `search:${searchResults[0].documentId}`);
                  if (e.key === 'Escape') setQuery('');
                }}
              />
            </div>
          </div>

          {documents.length === 0 && (
            <p className="rounded-md bg-muted p-3 text-sm">
              No documents in {library.folder.name} yet. Add hardware documents to that folder, then refresh.
            </p>
          )}
          {library.truncated && (
            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Large library: showing the first {documents.length} documents.
            </p>
          )}

          {searchResults ? (
            searchResults.length ? (
              <Section title={`${searchResults.length} match${searchResults.length === 1 ? '' : 'es'} · Enter inserts the first`}>
                {searchResults.map(row('search'))}
              </Section>
            ) : (
              <p className="px-2 text-sm text-muted-foreground">No hardware matches "{query}".</p>
            )
          ) : (
            <div className="space-y-4">
              {favoriteDocs.length > 0 && (
                <Section title={`Favorites (${favoriteDocs.length})`} collapsed={collapsed.favorites}
                  onToggle={() => toggleCollapsed('favorites')}>
                  {favoriteDocs.sort(rank).map(row('favorites'))}
                </Section>
              )}
              {recentDocs.length > 0 && (
                <Section title={`Recent (${recentDocs.length})`} collapsed={collapsed.recent}
                  onToggle={() => toggleCollapsed('recent')}>
                  {recentDocs.map(row('recent'))}
                </Section>
              )}
            </div>
          )}

          {documents.length > 0 && (
            <p className="text-[11px] text-muted-foreground">
              Click to insert. Parts land at the assembly origin, ready to mate. Star the ones you use most.
            </p>
          )}
        </>
      )}
    </div>
  );
}
